import { toast } from "sonner";

// Server and network errors stay up long enough to read on a busy floor
// (sonner's default ~4 s is gone before a worker looks back at the screen).
const ERROR_MS = 8000;

/**
 * Centralized error handler for axios calls in this app.
 *
 * The axios interceptor in axios.js already normalizes errors before rejecting:
 *   - Network/server errors  → { success: false, message: "Network or server error" }
 *   - Backend error responses → error.response.data  (e.g. { success, message, statusCode, ... })
 *   - Abort/cancel signals   → DOMException / CanceledError (name === "CanceledError" | "AbortError")
 *
 * @param {unknown} error    - The rejected value from an axios call.
 * @param {string}  [fallback] - Optional custom fallback message.
 */
const catchAxiosError = (error, fallback = "Something went wrong. Please try again.") => {
    // Silently ignore intentional request cancellations (AbortController)
    if (
        error?.name === "CanceledError" ||
        error?.name === "AbortError" ||
        error?.code === "ERR_CANCELED"
    ) {
        return;
    }

    // Development-only logging
    if (process.env.NODE_ENV === "development") {
        console.error("[catchAxiosError]", error);
    }

    const statusCode = error?.statusCode ?? error?.status;
    const backendMessage =
        typeof error?.message === "string" && error.message.trim()
            ? error.message.trim()
            : null;

    // 401 — handled by the axios interceptor (auto-logout). Guard just in case.
    if (statusCode === 401) {
        toast.error("Session expired. Please log in again.");
        return;
    }

    // 403 — permission denied
    if (statusCode === 403) {
        toast.error(backendMessage ?? "You do not have permission to perform this action.");
        return;
    }

    // 404 — resource not found
    if (statusCode === 404) {
        toast.error(backendMessage ?? "Resource not found.");
        return;
    }

    // 409 — books locked for that period (Models1/glModel lock check)
    if (statusCode === 409 && /are locked up to/i.test(backendMessage || "")) {
        toast.error("Books are locked for this period", {
            description: `${backendMessage} Ask someone with the books:override permission, or pick a date after the lock.`,
            duration: 9000,
        });
        return;
    }

    // 409 — conflict (e.g. duplicate entry)
    if (statusCode === 409) {
        toast.error(backendMessage ?? "A conflict occurred. The record may already exist.");
        return;
    }

    // 422 — validation error from backend
    if (statusCode === 422) {
        if (Array.isArray(error?.errors) && error.errors.length > 0) {
            error.errors.forEach((e) => {
                const msg = typeof e === "string" ? e : e?.message ?? JSON.stringify(e);
                toast.error(msg, { duration: 5000 });
            });
        } else {
            toast.error(backendMessage ?? "Validation failed. Please check your inputs.");
        }
        return;
    }

    // 5xx — server-side fault
    if (statusCode >= 500) {
        if (process.env.NODE_ENV === "development" && backendMessage) {
            // Show actual backend error in dev so you don't need to check the backend terminal
            toast.error(`[${statusCode}] ${backendMessage}`, { duration: 8000 });
        } else {
            toast.error("The server ran into a problem. Please try again; if it keeps happening, tell your admin what you were doing and when.", {
                description: new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
                duration: ERROR_MS,
            });
        }
        return;
    }

    // Backend sent a message we can display directly
    if (backendMessage) {
        toast.error(backendMessage, { duration: ERROR_MS });
        return;
    }

    // Pure network error (offline, CORS, timeout)
    if (error?.message === "Network or server error" || !statusCode) {
        toast.error("Unable to reach the server. Check your internet connection.", { duration: ERROR_MS });
        return;
    }

    // Last resort
    toast.error(fallback);
};

export default catchAxiosError;
